import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * RENTOS Global Error Boundary
 * Prevents white screen of death on unexpected errors.
 * Shows friendly fallback UI with refresh option.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });

    // Structured logging - log error silently
    const errorLog = {
      timestamp: new Date().toISOString(),
      type: 'REACT_ERROR_BOUNDARY',
      correlationId: `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      message: error?.message || 'Unknown error',
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Log to console in non-verbose way
    console.error('[RENTOS Error]', errorLog.correlationId, errorLog.message);

    // Could send to backend logging service here
    // fetch('/api/log/error', { method: 'POST', body: JSON.stringify(errorLog) });
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            {/* Error Icon */}
            <div className="w-20 h-20 rounded-full bg-[#EF4444]/20 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-[#EF4444]" />
            </div>

            {/* Error Message */}
            <h1 className="text-2xl font-bold text-white mb-2">
              Алдаа гарлаа
            </h1>
            <p className="text-[#6B7280] mb-6">
              Системд түр зуурын алдаа гарлаа. Хуудсыг дахин ачаалж үзнэ үү.
            </p>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRefresh}
                className="px-6 py-3 bg-[#2D8CFF] text-white rounded-xl font-medium flex items-center gap-2 hover:bg-[#2D8CFF]/90 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Дахин ачаалах
              </button>
              <button
                onClick={this.handleReset}
                className="px-6 py-3 bg-[#1F2937] text-[#9CA3AF] rounded-xl font-medium hover:bg-[#374151] transition-colors"
              >
                Дахин оролдох
              </button>
            </div>

            {/* Error Details - always show for debugging */}
            {this.state.error && (
              <details className="mt-8 text-left" open>
                <summary className="text-[#6B7280] text-sm cursor-pointer hover:text-white">
                  Алдааны мэдээлэл
                </summary>
                <pre className="mt-2 p-4 bg-[#111827] rounded-lg text-xs text-[#EF4444] overflow-auto max-h-48">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
